// Firebase functions for quiz and form submissions
import { 
  collection, 
  addDoc, 
  doc, 
  getDoc, 
  setDoc, 
  updateDoc, 
  query, 
  where, 
  getDocs,
  serverTimestamp
} from "firebase/firestore";
import { db, auth } from "./firebase-config";

// Service functions for data operations
export const saveQuizSubmission = async (quizData) => {
  try {
    // Log the connection status to verify Firebase is initialized correctly
    console.log("Firebase connection status:", !!db);
    
    // Handle both the old quiz format and the new integrated format
    const dataToSave = {
      ...quizData,
      created_at: serverTimestamp()
    };
    
    // Check if this is the integrated version with quizAnswers and userInfo
    if (quizData.quizAnswers && quizData.userInfo) {
      // This is from the integrated form - store in a special collection
      const docRef = await addDoc(collection(db, "waitlist"), dataToSave);
      console.log("Integrated submission saved with ID: ", docRef.id);
      return { success: true, id: docRef.id };
    } else {
      // This is from the original quiz format
      const docRef = await addDoc(collection(db, "quiz_submissions"), dataToSave);
      console.log("Quiz submission saved with ID: ", docRef.id);
      return { success: true, id: docRef.id };
    }
  } catch (error) {
    console.error("Error saving quiz submission: ", error);
    return { success: false, error };
  }
};

export const saveUserFormSubmission = async (userData) => {
  try {
    const docRef = await addDoc(collection(db, "user_submissions"), {
      ...userData,
      created_at: serverTimestamp()
    });
    console.log("User form submission saved with ID: ", docRef.id);
    return { success: true, id: docRef.id };
  } catch (error) {
    console.error("Error saving user form submission: ", error);
    return { success: false, error };
  }
};

// Save video metadata to user submission
export const saveVideoSubmission = async (userId, submissionId, videoData) => {
  try {
    console.log(`Saving video metadata for user ${userId}, submission ${submissionId}`);
    
    // Update the existing submission with video data
    const submissionRef = doc(db, "waitlist", submissionId);
    
    // First get the existing submission
    const submissionSnap = await getDoc(submissionRef);
    
    if (!submissionSnap.exists()) {
      throw new Error("Submission not found");
    }
    
    // Prepare video submission data
    const videoSubmission = {
      videoUrl: videoData.videoUrl,
      storagePath: videoData.storagePath,
      timestamp: videoData.timestamp,
      lastUpdated: serverTimestamp()
    };
    
    // Update the submission document with video data
    await updateDoc(submissionRef, {
      videoSubmission: videoSubmission
    });
    
    console.log("Video metadata saved successfully");
    
    return { 
      success: true, 
      data: videoSubmission
    };
  } catch (error) {
    console.error("Error saving video metadata:", error);
    return { success: false, error: error.message };
  }
};

// Link a submission to a user account
export const linkSubmissionToUser = async (userId, submissionId) => {
  try {
    console.log(`Linking submission ${submissionId} to user ${userId}`);
    
    // Create a document in a users-submissions mapping collection
    await setDoc(doc(db, "user_submissions_map", userId), {
      submissionId,
      linkedAt: serverTimestamp()
    });
    
    console.log("Created mapping document");
    
    // Also update the original submission with the user ID
    const submissionRef = doc(db, "waitlist", submissionId);
    const submissionSnap = await getDoc(submissionRef);
    
    if (!submissionSnap.exists()) {
      throw new Error("Submission not found");
    }
    
    const submissionData = submissionSnap.data();
    
    // Update the submission with userId
    await updateDoc(submissionRef, {
      userId,
      linkedAt: serverTimestamp()
    });
    
    console.log("Updated submission with userId");
    
    // Copy waitlist data to user_profiles
    const profileRef = doc(db, "user_profiles", userId);
    const profileSnap = await getDoc(profileRef);
    
    // Prepare profile data from waitlist submission
    const profileData = {
      // Basic info from userInfo if available
      basicInfo: submissionData.userInfo || {},
      // Include quiz answers if available
      preferences: submissionData.quizAnswers || {},
      // Copy video submission data if available
      videoSubmission: submissionData.videoSubmission || null,
      // Copy resume data if available
      resumeUrl: submissionData.resume?.resumeUrl || null,
      resumeFilename: submissionData.resume?.filename || null,
      resumeLastUpdated: submissionData.resume?.lastUpdated ? new Date(submissionData.resume.lastUpdated).toISOString() : null,
      resumeStoragePath: submissionData.resume?.storagePath || null,
      // Copy profile links if available
      profileLinks: submissionData.profileLinks || {},
      // Include waitlist record reference for backward compatibility
      waitlistSubmissionId: submissionId,
      // Include timestamps
      lastUpdated: serverTimestamp(),
    };
    
    // If profile doesn't exist, create it. Otherwise update it.
    if (!profileSnap.exists()) {
      await setDoc(profileRef, {
        ...profileData,
        userId,
        createdAt: serverTimestamp()
      });
      console.log("Created new user profile from waitlist data");
    } else {
      // Only update profile if it exists but doesn't have waitlist data yet
      if (!profileSnap.data().waitlistSubmissionId) {
        await updateDoc(profileRef, profileData);
        console.log("Updated existing user profile with waitlist data");
      } else {
        console.log("User profile already has waitlist data, skipping update");
      }
    }
    
    return { success: true };
  } catch (error) {
    console.error("Error linking submission to user: ", error);
    return { success: false, error };
  }
};

// Get a user's submission - enhanced with better logging
export const getUserSubmission = async (userId) => {
  console.log('Fetching submission for user:', userId);
  
  try {
    // First, check if there's a mapping document
    console.log('Checking user_submissions_map collection for user ID:', userId);
    const mapDocRef = doc(db, "user_submissions_map", userId);
    const mapDocSnap = await getDoc(mapDocRef);
    
    console.log('Mapping document exists:', mapDocSnap.exists());
    
    let submissionData = null;
    let submissionId = null;
    
    if (mapDocSnap.exists()) {
      const mapData = mapDocSnap.data();
      console.log('Found mapping data:', mapData);
      submissionId = mapData.submissionId;
      
      if (!submissionId) {
        console.error('No submissionId found in mapping document');
        return { success: false, error: "Submission mapping exists but no submissionId found" };
      }
      
      console.log('Fetching submission with ID:', submissionId);
      const submissionRef = doc(db, "waitlist", submissionId);
      const submissionSnap = await getDoc(submissionRef);
      
      console.log('Submission document exists:', submissionSnap.exists());
      
      if (submissionSnap.exists()) {
        submissionData = submissionSnap.data();
        console.log('Submission data retrieved successfully');
      } else {
        console.error('Submission document not found');
        return { success: false, error: "Submission document not found" };
      }
    }
    
    // If mapping doesn't exist or no submission data, try direct query on waitlist
    if (!submissionData) {
      console.log('No mapping found or no submission data, trying to query waitlist directly by userId');
      
      const submissionsRef = collection(db, "waitlist");
      const q = query(submissionsRef, where("userId", "==", userId));
      const querySnapshot = await getDocs(q);
      
      console.log('Direct query found results:', !querySnapshot.empty);
      
      if (!querySnapshot.empty) {
        // Use the first matching document
        const docSnap = querySnapshot.docs[0];
        console.log('Using first matching document, ID:', docSnap.id);
        submissionData = docSnap.data();
        submissionId = docSnap.id;
        
        // Create a mapping for future lookups
        await setDoc(mapDocRef, {
          submissionId: docSnap.id,
          linkedAt: serverTimestamp()
        });
        
        console.log('Created new mapping document');
      }
    }
    
    // If still not found, try searching by email in userInfo
    if (!submissionData) {
      console.log('No results by userId, trying to get user email');
      
      // Get the user email (without using getAuth().getUser which is admin-only)
      const userEmail = auth.currentUser?.email;
      
      if (userEmail) {
        console.log('Found user email:', userEmail);
        
        const submissionsRef = collection(db, "waitlist");
        const emailQuery = query(
          submissionsRef, 
          where("userInfo.email", "==", userEmail)
        );
        
        const emailQuerySnapshot = await getDocs(emailQuery);
        console.log('Email query found results:', !emailQuerySnapshot.empty);
        
        if (!emailQuerySnapshot.empty) {
          const docSnap = emailQuerySnapshot.docs[0];
          console.log('Using first matching document by email, ID:', docSnap.id);
          submissionData = docSnap.data();
          submissionId = docSnap.id;
          
          // Create a mapping for future lookups
          await setDoc(mapDocRef, {
            submissionId: docSnap.id,
            linkedAt: serverTimestamp()
          });
          
          console.log('Created new mapping document');
          
          // Update the submission with userId
          await updateDoc(doc(db, "waitlist", docSnap.id), {
            userId: userId,
            linkedAt: serverTimestamp()
          });
          
          console.log('Updated submission with userId');
        }
      } else {
        console.log('Could not get user email from current auth state');
      }
    }
    
    if (submissionData) {
      // Add resume data from submission if available
      const enhancedData = {
        ...submissionData,
        profileLinks: {
          ...(submissionData.profileLinks || {}),
        }
      };
      
      return { success: true, data: enhancedData, submissionId };
    }
    
    // If we still don't have a user profile, create one
    console.log('No submission found for user');
    return { success: false, error: "No submission found for this user" };
    
  } catch (error) {
    console.error("Error getting user submission:", error);
    return { success: false, error: error.message };
  }
};

// Update a user's career preferences
export const updateUserPreferences = async (userId, submissionId, updatedQuizAnswers) => {
  try {
    console.log('Updating preferences for user:', userId, 'submission:', submissionId);
    console.log('New preferences:', updatedQuizAnswers);
    
    // Reference to the submission document
    const submissionRef = doc(db, "waitlist", submissionId);
    
    // Get the current document data
    const submissionSnap = await getDoc(submissionRef);
    
    if (!submissionSnap.exists()) {
      console.error('Submission document not found');
      return { success: false, error: "Submission document not found" };
    }
    
    // Get current data
    const currentData = submissionSnap.data();
    
    // Update only the quizAnswers field
    await updateDoc(submissionRef, {
      quizAnswers: updatedQuizAnswers,
      lastUpdated: serverTimestamp()
    });
    
    console.log('Preferences updated successfully');
    
    // Return the updated data
    return { 
      success: true, 
      data: {
        ...currentData,
        quizAnswers: updatedQuizAnswers,
        lastUpdated: new Date().toISOString()
      }
    };
  } catch (error) {
    console.error('Error updating preferences:', error);
    return { success: false, error: error.message };
  }
};